/**
 * Customer Detail Modal
 *
 * Shows comprehensive customer information including:
 * - Contact details
 * - Call history with notes
 * - Job/deal history
 * - Activity timeline
 */

import { getLocaleSync } from '@/utils/locale';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { X, Phone, Mail, MapPin, Calendar, Briefcase, User, Clock, Wifi, UserPlus, Edit2, Trash2, Star, Zap, AlertTriangle, ExternalLink, Plus, Home, Building2, Copy, Check, ChevronRight, Paperclip, MessageSquare, DollarSign, Maximize2, AtSign } from 'lucide-react';
import { AttachmentsSection } from './attachments/AttachmentsSection';
import { ConfirmModal } from './common/ConfirmModal';
import { FaultIntakeModal } from './faults/FaultIntakeModal';
import { DealCreationWizard } from './deals/DealCreationWizard';
import { ServiceRequestIntakeModal } from './service-requests/ServiceRequestIntakeModal';
import { LogCallModal } from './LogCallModal';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { collection, query, where, getDocs, orderBy, doc as firestoreDoc, getDoc, onSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../firebase';
import { CallLog, Job, CustomerService, Contact, CustomerAddress } from '../types/schema';
import { Customer, Note } from '@/types';
import type { Deal } from '@/types/theo-workflow.types';
import { subscribeNotesForEntity, createNote } from '@/services/notes.service';
import { useTeam } from '@/hooks/useTeam';
import type { MentionableUser } from '@/types/mention.types';
import {
  applyMention,
  findMentionTrigger,
  matchesMentionQuery,
  mentionHandleFor,
} from '@/utils/mention-trigger';
import { suspendService, reactivateService, cancelService } from '../services/customer-service.service';
import { syncServiceAddresses, removeCustomerAddress, getChildCustomers, deleteCustomer, linkCustomerToCompany } from '../services/customer.service';
import { CustomerAddressBook } from './customers/CustomerAddressBook';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';
import { setPrimaryAccountContact, setPrimarySiteContact, contactHasRole } from '../services/contact.service';
import type { Fault } from '../types/fault.types';
import { useCustomerContacts, useContactMutations } from '../hooks/useContacts';
import { CallDetailModal } from './CallDetailModal';
import { JobDetailModal } from './JobDetailModal';
import { PhoneNumber } from './PhoneNumber';
import { ContactFormModal } from './ContactFormModal';
import { AddCustomerServiceModal } from './AddCustomerServiceModal';
import { EditServiceModal } from './EditServiceModal';
import { EditCustomerModal } from './EditCustomerModal';
import { AddCustomerModal } from './AddCustomerModal';
import { LinkExistingCustomerModal } from './LinkExistingCustomerModal';
import { FaultDetailsView } from './faults/FaultDetailsView';
import { DealQuickViewModal } from './deals/DealQuickViewModal';
import { ProposalsCard } from './customers/ProposalsCard';
import { InvoicesCard } from './customers/InvoicesCard';
import { CustomerHistoryTimeline } from './customers/CustomerHistoryTimeline';
import { CustomerHistoryModal } from './customers/CustomerHistoryModal';
import { SiteVaultCard } from './customers/SiteVaultCard';
import {
  CustomerCreateMenu,
  CustomerCreateActions,
  type CustomerCreateAction,
} from './customers/CustomerCreateMenu';
import {
  getCustomerAggregates,
  type CustomerAggregates,
} from '@/services/aggregate-customer.service';
import { formatCurrency } from '@/utils/pricing';
import { useAllServicePlans } from '@/hooks/useServicePlans';
import { resolveServiceSpeed, SPEED_SOURCE_TITLE } from '@/utils/planSpeed';

interface CustomerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onCustomerUpdated?: (updated: Customer) => void;
  onCustomerDeleted?: (deletedId: string) => void;
  /**
   * Optional Tailwind z-index class for the slide-over container. Default
   * `'z-[200]'` (page-level usage from SchedulePage / CustomersPage).
   * The CustomerProfileProvider passes `'z-[400]'` so cross-entity opens
   * (e.g. clicking the customer name inside a JobDetailModal at z-250)
   * still render the customer panel ON TOP. Customer 360 Sprint Day 1.
   */
  zIndexClass?: string;
}

export function CustomerDetailModal({
  isOpen,
  onClose,
  customer: seedCustomer,
  onCustomerUpdated,
  onCustomerDeleted,
  zIndexClass = 'z-[200]',
}: CustomerDetailModalProps) {
  // In-modal navigation (Phase 5) — the displayed customer is now
  // internal state initialised from the `seedCustomer` prop. The
  // existing 100+ reads of `customer.*` still resolve against this
  // state, so the rest of the component is unchanged. navStack holds
  // the breadcrumb when the user drills into a child site or jumps to
  // a parent company; popping it returns to the previous level.
  const [customer, setCustomer] = useState<Customer | null>(seedCustomer);
  const [navStack, setNavStack] = useState<Customer[]>([]);

  // Reset to the seed whenever the parent opens us with a different
  // customer (or simply re-opens with the same one). Drops any in-flight
  // nav drill so the user never lands on a stale child.
  useEffect(() => {
    if (!isOpen) return;
    setCustomer(seedCustomer);
    setNavStack([]);
  }, [isOpen, seedCustomer?.id]);

  // Fetch + push current onto the stack + replace displayed. No-op when
  // the target is already the current customer (e.g. user double-taps
  // the breadcrumb).
  const navigateToCustomer = async (targetId: string) => {
    if (!targetId || targetId === customer?.id) return;
    try {
      const snap = await getDoc(firestoreDoc(db, 'customers', targetId));
      if (!snap.exists()) {
        console.warn('[CustomerDetailModal] navigateToCustomer: customer not found', targetId);
        return;
      }
      const next = { id: snap.id, ...snap.data() } as Customer;
      setNavStack((prev) => (customer ? [...prev, customer] : prev));
      setCustomer(next);
    } catch (err) {
      console.error('[CustomerDetailModal] navigateToCustomer failed:', err);
    }
  };

  // Step back one level in the breadcrumb.
  const popNav = () => {
    setNavStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev[prev.length - 1];
      setCustomer(next);
      return prev.slice(0, -1);
    });
  };

  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [customerFaults, setCustomerFaults] = useState<Fault[]>([]);
  const [services, setServices] = useState<CustomerService[]>([]);
  const [serviceRequests, setServiceRequests] = useState<import('../types/service-request.types').ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  // Phase 2: collapsed 9 tabs → 4. Information (default) merges old Info +
  // Sites (companies). Activity merges old Calls / Jobs / Deals / Faults /
  // History into one filterable timeline. Services + Files unchanged.
  const [activeTab, setActiveTab] = useState<'info' | 'activity' | 'services' | 'files'>('info');
  // Feature-pack gate: the Services tab is an MSP surface — hidden (and its
  // keyboard shortcut disabled) for tenants without the customer-services
  // feature. docs/FEATURE_PACKS_PLAN.md.
  const hasCustomerServices = useFeatureGate('customer-services');
  // Service plans back the speed/data shown against each service — the service
  // doc only denormalises `planName`. This modal is mounted app-wide by
  // CustomerProfileProvider, so the listener only opens on the Services tab.
  const { plans: servicePlans } = useAllServicePlans(isOpen && activeTab === 'services');
  const servicePlansById = useMemo(
    () => new Map(servicePlans.filter((p) => p.id).map((p) => [p.id!, p])),
    [servicePlans],
  );
  const [childCustomers, setChildCustomers] = useState<Customer[]>([]);
  const [copiedAccountNumber, setCopiedAccountNumber] = useState(false);
  const [isAddSiteOpen, setIsAddSiteOpen] = useState(false);
  const [isLinkExistingOpen, setIsLinkExistingOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  // Day 3: deals previously opened via window.open(_, '_blank') losing context.
  // Now renders in-modal via DealQuickViewModal — symmetric with jobs/faults.
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const navigate = useNavigate();
  // Day 4: Customer 360 roll-ups (signed proposals + open invoices). Sync
  // today (derived from already-loaded `deals[]`); Day 5 adds async audit
  // entries to the same payload.
  const [aggregates, setAggregates] = useState<CustomerAggregates | null>(null);
  const [aggregatesLoading, setAggregatesLoading] = useState(true);
  const [customerNotes, setCustomerNotes] = useState<Note[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [showNoteMentions, setShowNoteMentions] = useState(false);
  const [noteMentionSearch, setNoteMentionSearch] = useState('');
  const noteDraftRef = useRef<HTMLTextAreaElement>(null);

  // Team list for the note composer's @tag picker. Dual-access users appear as
  // Office — mentions route to their office persona.
  const { team } = useTeam();
  const mentionableUsers: MentionableUser[] = useMemo(
    () =>
      team
        .filter((m) => m.active !== false)
        .map((m) => ({
          id: m.id,
          name: m.name,
          role: m.dualAccess ? 'Office' : m.role,
          isActive: m.active,
        })),
    [team],
  );
  const [savingNote, setSavingNote] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Contact management
  const { contacts } = useCustomerContacts(customer?.id || null);
  const { deleteContact } = useContactMutations();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Service management
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [editServiceTarget, setEditServiceTarget] = useState<CustomerService | null>(null);

  // Address management — the card itself is CustomerAddressBook; this level
  // owns the list state (shared with the contacts tab + ServicesCard) and the
  // themed remove confirmation.
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);

  const [selectedFault, setSelectedFault] = useState<Fault | null>(null);

  // Quick-action launchers (Phase 1) + Log call (Phase 5) + service request
  // (Create menu). All three intake modals take preSelectedCustomerId, so the
  // operator never re-searches for the customer they already have open.
  const [isFaultIntakeOpen, setIsFaultIntakeOpen] = useState(false);
  const [isDealWizardOpen, setIsDealWizardOpen] = useState(false);
  const [isServiceRequestIntakeOpen, setIsServiceRequestIntakeOpen] = useState(false);
  const [isLogCallOpen, setIsLogCallOpen] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  // True while the "New" dropdown is open — feeds useModalAccessibility's
  // escapeDisabled so Escape dismisses the menu without also closing this
  // modal (both listen on document; stopPropagation can't order them).
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  // Full-screen history timeline (V107.53) — launched from the Activity tab.
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // Themed confirm flow (Phase 1 — replaces window.confirm + window.prompt for
  // suspend / cancel / reactivate / delete-customer / delete-contact /
  // remove-address). One state shape, one ConfirmModal render — see runConfirm().
  type PendingConfirm =
    | { kind: 'suspend-service'; serviceId: string; serviceName: string }
    | { kind: 'cancel-service'; serviceId: string; serviceName: string }
    | { kind: 'reactivate-service'; serviceId: string; serviceName: string }
    | { kind: 'delete-customer' }
    | { kind: 'delete-contact'; contactId: string; contactName: string }
    | { kind: 'remove-address'; addressId: string; addressLabel: string; fromService: boolean };
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Fetch customer data when modal opens (everything except services).
  // Day-4-fix-pass: reset aggregates state up front so the previous
  // customer's proposals/invoices don't flash for one render before
  // skeleton kicks in.
  useEffect(() => {
    if (isOpen && customer?.id) {
      setAggregates(null);
      setAggregatesLoading(true);
      loadCustomerData();
    }
  }, [isOpen, customer?.id]);

  // Services subscription (Phase 3) — real-time so Suspend / Cancel /
  // Reactivate feel instant without re-fetching everything else.
  // Replaces the one-shot getCustomerServices call that used to live
  // inside loadCustomerData.
  // Skipped entirely for tenants without the customer-services (MSP) pack:
  // this modal is mounted app-wide by CustomerProfileProvider, so an ungated
  // listener would be wasted reads on every customer open — and leaving
  // `services` populated would keep rendering the header MRR chip and the
  // service entries in the Activity timeline, i.e. the MSP data the pack gate
  // is supposed to remove. Emptying it here removes all three at once.
  useEffect(() => {
    if (!isOpen || !customer?.id || !hasCustomerServices) {
      setServices([]);
      return;
    }
    const q = query(
      collection(db, 'customer_services'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('customerId', '==', customer.id),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setServices(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as CustomerService[]);
      },
      (err) => {
        console.error('[CustomerDetailModal] services subscription error:', err);
      },
    );
    return () => unsub();
  }, [isOpen, customer?.id, hasCustomerServices]);

  // Customer notes (task 21) — reference notes the office writes ON the
  // customer, surfaced in the Activity timeline + filterable. Real-time so a
  // freshly-added note appears without a refetch.
  useEffect(() => {
    if (!isOpen || !customer?.id) {
      setCustomerNotes([]);
      return;
    }
    const unsub = subscribeNotesForEntity(getTenantIdSync(), 'customer', customer.id, (n) => {
      setCustomerNotes(n);
    });
    return () => unsub();
  }, [isOpen, customer?.id]);

  // Load child customers for company records (for the Sites tab + badge count)
  useEffect(() => {
    if (!isOpen || !customer?.id || !customer.isCompany) {
      setChildCustomers([]);
      return;
    }
    getChildCustomers(customer.id)
      .then(setChildCustomers)
      .catch(() => setChildCustomers([]));
  }, [isOpen, customer?.id, customer?.isCompany]);

  // Keyboard shortcuts (Phase 4) — ⌘/Ctrl + 1-4 switches tabs without
  // forcing the user to mouse over. Ignored when focus is in an input
  // or textarea (so typing "2" into the activity search doesn't jump
  // tabs). Esc is handled by useModalAccessibility above.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) return;
      const target = e.target as HTMLElement | null;
      if (target && /INPUT|TEXTAREA|SELECT/.test(target.tagName)) return;
      if (target && target.isContentEditable) return;
      const tabByKey: Record<string, 'info' | 'activity' | 'services' | 'files'> = {
        '1': 'info',
        '2': 'activity',
        '3': 'services',
        '4': 'files',
      };
      const next = tabByKey[e.key];
      if (next && (next !== 'services' || hasCustomerServices)) {
        e.preventDefault();
        setActiveTab(next);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, hasCustomerServices]);

  const handleCopyAccountNumber = async () => {
    if (!customer?.accountNumber) return;
    try {
      await navigator.clipboard.writeText(customer.accountNumber);
      setCopiedAccountNumber(true);
      setTimeout(() => setCopiedAccountNumber(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Launcher — actual work happens inside runConfirm() so the
  // ConfirmModal owns the confirmation copy + reason flow consistently
  // with every other destructive action in this modal.
  const handleDeleteCustomer = () => {
    if (!customer?.id) return;
    setPendingConfirm({ kind: 'delete-customer' });
  };

  // Phase 3: Per-service fault map is now DERIVED from the already-loaded
  // customerFaults state. Eliminates the N+1 per-service-id Firestore
  // query that used to fire on every Services tab open. The third leg of
  // the fault union query (faults by serviceId IN [...services]) still
  // runs — see the supplemental useEffect below — and merges its results
  // into customerFaults, so this map stays correct for transferred-service
  // edge cases too.
  const serviceFaults = useMemo(() => {
    const map: Record<string, Fault[]> = {};
    for (const fault of customerFaults) {
      if (fault.serviceId) {
        (map[fault.serviceId] ||= []).push(fault);
      }
    }
    return map;
  }, [customerFaults]);

  // Phase 3: serviceId-fault fallback. Runs after services come in from
  // the real-time subscription. Catches faults that travel with a
  // transferred service even when the customer link on the fault is
  // stale. Idempotent — dedupes against existing customerFaults by id.
  // Firestore `in` supports up to 30 values; we slice defensively.
  useEffect(() => {
    if (!isOpen || !customer?.id || services.length === 0) return;
    const serviceIds = services
      .map((s) => s.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .slice(0, 30);
    if (serviceIds.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'faults'),
          where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
          where('serviceId', 'in', serviceIds),
        ));
        if (cancelled) return;
        setCustomerFaults((prev) => {
          const seen = new Set(prev.map((f) => f.id));
          const extras: Fault[] = [];
          snap.docs.forEach((d) => {
            if (!seen.has(d.id)) {
              extras.push({ id: d.id, ...d.data() } as Fault);
            }
          });
          if (extras.length === 0) return prev;
          return [...prev, ...extras].sort((a, b) =>
            (b.createdAt || '').localeCompare(a.createdAt || ''),
          );
        });
      } catch (err) {
        console.error('[CustomerDetailModal] faults by serviceId query failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, customer?.id, services]);

  // @tag picker for the customer note composer — same shared trigger rules as
  // every other notes surface (src/utils/mention-trigger.ts).
  const syncNoteMentions = (val: string) => {
    const cursor = noteDraftRef.current?.selectionStart ?? val.length;
    const trigger = findMentionTrigger(val, cursor);
    if (!trigger) {
      setShowNoteMentions(false);
      return;
    }
    setNoteMentionSearch(trigger.query);
    setShowNoteMentions(true);
  };

  const insertNoteMention = (user: MentionableUser) => {
    const cursor = noteDraftRef.current?.selectionStart ?? noteDraft.length;
    const trigger = findMentionTrigger(noteDraft, cursor);
    if (!trigger) return;

    const next = applyMention(
      noteDraft,
      cursor,
      trigger.atIndex,
      mentionHandleFor(user, mentionableUsers),
    );
    setNoteDraft(next.text);
    setShowNoteMentions(false);
    setTimeout(() => {
      noteDraftRef.current?.focus();
      noteDraftRef.current?.setSelectionRange(next.cursor, next.cursor);
    }, 0);
  };

  const noteMentionMatches = mentionableUsers
    .filter((u) => matchesMentionQuery(u, noteMentionSearch))
    .slice(0, 6);

  const handleAddNote = async () => {
    const content = noteDraft.trim();
    if (!content || !customer?.id || savingNote) return;
    setSavingNote(true);
    try {
      await createNote({
        tenantId: getTenantIdSync(),
        entityType: 'customer',
        entityId: customer.id,
        content,
        source: 'other',
        createdBy: localStorage.getItem('user_id') || 'office',
        createdByName: localStorage.getItem('logged_in_user') || 'Office',
        entityTitle: `Customer: ${customer.name}`,
        // Without this the @handles typed above are plain text and nobody is
        // notified — createNote only fans out when it has a user list to
        // resolve handles against.
        availableUsers: mentionableUsers,
      });
      setNoteDraft('');
      setShowNoteMentions(false);
    } catch (err) {
      console.error('[CustomerDetailModal] add note failed:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const loadCustomerData = async () => {
    if (!customer?.id) return;

    setLoading(true);
    // Reset stale state immediately so the previous customer's data
    // doesn't flash through while we fetch. Services are owned by the
    // real-time subscription effect and reset themselves on
    // customer.id change, so we skip them here.
    setCallLogs([]);
    setJobs([]);
    setDeals([]);
    setCustomerFaults([]);
    setServiceRequests([]);
    setCustomerAddresses(customer.addresses || []);

    const customerId = customer.id;
    const customerName = customer.name;
    const tenantUnion = tenantIdReadUnion(getTenantIdSync());

    // Phase 3 — parallelize. The seven independent queries that used to
    // run sequentially (3 jobs, 3 faults, 1 calls, 1 deals — services
    // now have their own real-time subscription) fire in a single
    // round-trip. Each lane uses Promise.allSettled so one slow / failing
    // query doesn't block the rest of the modal from rendering.
    const jobsByIdQuery = getDocs(query(
      collection(db, 'jobs'),
      where('tenantId', 'in', tenantUnion),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
    ));
    const jobsByNameQuery = customerName
      ? getDocs(query(
          collection(db, 'jobs'),
          where('tenantId', 'in', tenantUnion),
          where('client', '==', customerName),
        ))
      : Promise.resolve(null);
    const dealsQuery = getDocs(query(
      collection(db, 'deals'),
      where('tenantId', 'in', tenantUnion),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
    ));
    // call_logs is a strict tenant-isolated collection (firestore.rules) — a
    // list query WITHOUT a tenantId filter is rejected wholesale with "Missing
    // or insufficient permissions", even when every doc is in-tenant. Mirror
    // the sibling queries: filter by tenantId and sort client-side (an orderBy
    // alongside the `tenantId in` filter would need a new composite index).
    const callsQuery = getDocs(query(
      collection(db, 'call_logs'),
      where('tenantId', 'in', tenantUnion),
      where('customerId', '==', customerId),
    ));
    // Fault union (1) by customerId, (2) by clientName.
    // (3) by serviceId is handled in a follow-up effect after services
    // are first loaded — see the supplemental useEffect below.
    const faultsByIdQuery = getDocs(query(
      collection(db, 'faults'),
      where('tenantId', 'in', tenantUnion),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
    ));
    const faultsByNameQuery = customerName
      ? getDocs(query(
          collection(db, 'faults'),
          where('tenantId', 'in', tenantUnion),
          where('clientName', '==', customerName),
        ))
      : Promise.resolve(null);
    // Service requests — previously invisible from the customer hub entirely.
    // tenantId filter keeps this valid under a future strict isolation rule.
    const serviceRequestsQuery = getDocs(query(
      collection(db, 'service_requests'),
      where('tenantId', 'in', tenantUnion),
      where('customerId', '==', customerId),
    ));

    try {
      const [
        jobsByIdResult,
        jobsByNameResult,
        dealsResult,
        callsResult,
        faultsByIdResult,
        faultsByNameResult,
        serviceRequestsResult,
      ] = await Promise.allSettled([
        jobsByIdQuery,
        jobsByNameQuery,
        dealsQuery,
        callsQuery,
        faultsByIdQuery,
        faultsByNameQuery,
        serviceRequestsQuery,
      ]);

      // --- Service requests
      if (serviceRequestsResult.status === 'fulfilled' && serviceRequestsResult.value) {
        const reqs = serviceRequestsResult.value.docs
          .map((d) => ({ id: d.id, ...d.data() } as { id: string; createdAt?: string }))
          .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        setServiceRequests(reqs as import('../types/service-request.types').ServiceRequest[]);
      } else if (serviceRequestsResult.status === 'rejected') {
        console.error('[CustomerDetailModal] service_requests query failed:', serviceRequestsResult.reason);
      }

      // --- Calls (sorted client-side — the query drops orderBy to avoid a
      // composite index alongside the `tenantId in` filter). timestamp is a
      // Firestore Timestamp on new rows and an ISO string on legacy rows.
      if (callsResult.status === 'fulfilled' && callsResult.value) {
        const toMillis = (t: unknown): number => {
          if (t && typeof (t as { toMillis?: unknown }).toMillis === 'function') {
            return (t as { toMillis(): number }).toMillis();
          }
          if (typeof t === 'string') {
            const ms = Date.parse(t);
            return Number.isFinite(ms) ? ms : 0;
          }
          return 0;
        };
        const calls = (callsResult.value.docs.map((d) => ({ id: d.id, ...d.data() })) as CallLog[])
          .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
        setCallLogs(calls);
      } else if (callsResult.status === 'rejected') {
        console.error('[CustomerDetailModal] calls query failed:', callsResult.reason);
      }

      // --- Jobs (merge id-match + name-match, dedup by doc id, sort newest first)
      const jobsByIdSet = new Set<string>();
      const jobsData: Job[] = [];
      if (jobsByIdResult.status === 'fulfilled' && jobsByIdResult.value) {
        jobsByIdResult.value.docs.forEach((d) => {
          jobsByIdSet.add(d.id);
          jobsData.push({ id: d.id, ...d.data() } as Job);
        });
      } else if (jobsByIdResult.status === 'rejected') {
        console.error('[CustomerDetailModal] jobs by customerId query failed:', jobsByIdResult.reason);
      }
      if (jobsByNameResult.status === 'fulfilled' && jobsByNameResult.value) {
        jobsByNameResult.value.docs.forEach((d) => {
          if (!jobsByIdSet.has(d.id)) jobsData.push({ id: d.id, ...d.data() } as Job);
        });
      }
      jobsData.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setJobs(jobsData);

      // --- Deals
      const dealsArray: Deal[] = dealsResult.status === 'fulfilled' && dealsResult.value
        ? (dealsResult.value.docs.map((d) => ({ id: d.id, ...d.data() })) as Deal[])
        : [];
      setDeals(dealsArray);
      if (dealsResult.status === 'rejected') {
        console.error('[CustomerDetailModal] deals query failed:', dealsResult.reason);
      }

      // Day 4: customer 360 roll-ups derived from the deals we just loaded.
      // Promise.allSettled inside the service isolates per-lane failures
      // (e.g. a future audit query failing mustn't blank the proposals card).
      setAggregatesLoading(true);
      getCustomerAggregates({ customerId, deals: dealsArray })
        .then((next) => setAggregates(next))
        .catch((err) =>
          console.error('[CustomerDetailModal] getCustomerAggregates failed:', err),
        )
        .finally(() => setAggregatesLoading(false));

      // --- Faults (merge id-match + name-match; serviceId fallback runs later)
      const faultsSeen = new Set<string>();
      const faultsData: Fault[] = [];
      const addFault = (docId: string, data: DocumentData) => {
        if (!faultsSeen.has(docId)) {
          faultsSeen.add(docId);
          faultsData.push({ id: docId, ...data } as Fault);
        }
      };
      if (faultsByIdResult.status === 'fulfilled' && faultsByIdResult.value) {
        faultsByIdResult.value.docs.forEach((d) => addFault(d.id, d.data()));
      } else if (faultsByIdResult.status === 'rejected') {
        console.error('[CustomerDetailModal] faults by customerId query failed:', faultsByIdResult.reason);
      }
      if (faultsByNameResult.status === 'fulfilled' && faultsByNameResult.value) {
        faultsByNameResult.value.docs.forEach((d) => addFault(d.id, d.data()));
      }
      faultsData.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setCustomerFaults(faultsData);

      // Sync service addresses into customer.addresses (fire-and-forget).
      // Doesn't block the modal — if it backfills new addresses we
      // refresh the local addresses state.
      syncServiceAddresses(customerId).then(async (added) => {
        if (added > 0) {
          const snap = await getDoc(firestoreDoc(db, 'customers', customerId));
          if (snap.exists()) {
            setCustomerAddresses((snap.data() as Customer).addresses || []);
          }
        }
      }).catch((err) => console.error('[CustomerDetailModal] syncServiceAddresses failed:', err));
    } catch (error: unknown) {
      console.error('[CustomerDetailModal] Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(getLocaleSync(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getCallTypeColor = (type: string) => {
    // Keys must match CallLog['callType'] in src/types/schema.ts:
    // 'sales' | 'fault' | 'enquiry' | 'billing'.
    // (Pre-Phase-5 versions used 'query' / 'support' which silently
    // dropped enquiry + billing calls into the grey fallback.)
    const colors = {
      sales:   'bg-green-500/20 text-green-400 border-green-500/30',
      fault:   'bg-red-500/20 text-red-400 border-red-500/30',
      enquiry: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      billing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      other:   'bg-white/10 text-white/60 border-white/15',
    };
    return colors[type as keyof typeof colors] || colors.other;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'Unscheduled': 'bg-yellow-500/20 text-yellow-400',
      'Scheduled': 'bg-blue-500/20 text-blue-400',
      'In Progress': 'bg-purple-500/20 text-purple-400',
      'Completed': 'bg-green-500/20 text-green-400',
      'Cancelled': 'bg-red-500/20 text-red-400'
    };
    return colors[status as keyof typeof colors] || 'bg-white/10 text-white/60';
  };

  // Contact handlers
  const handleAddContact = () => {
    setEditingContact(null);
    setIsContactFormOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setIsContactFormOpen(true);
  };

  // All destructive / state-changing actions funnel through the themed
  // ConfirmModal (see runConfirm + the modal JSX at the bottom of the file).
  // Replaces the previous window.prompt / window.confirm pattern.
  const handleDeleteContact = (contact: Contact) => {
    if (!contact.id) return;
    setPendingConfirm({
      kind: 'delete-contact',
      contactId: contact.id,
      contactName: contact.name || 'this contact',
    });
  };

  const handleSuspendService = (service: CustomerService) => {
    if (!service.id) return;
    setPendingConfirm({
      kind: 'suspend-service',
      serviceId: service.id,
      serviceName: service.planName || service.serviceType || 'this service',
    });
  };

  const handleReactivateService = (service: CustomerService) => {
    if (!service.id) return;
    setPendingConfirm({
      kind: 'reactivate-service',
      serviceId: service.id,
      serviceName: service.planName || service.serviceType || 'this service',
    });
  };

  const handleCancelService = (service: CustomerService) => {
    if (!service.id) return;
    setPendingConfirm({
      kind: 'cancel-service',
      serviceId: service.id,
      serviceName: service.planName || service.serviceType || 'this service',
    });
  };

  /** Re-read the customer doc after an address write. The address book writes
   *  through the service layer, which owns primary/legacy-mirror bookkeeping,
   *  so we re-read rather than patch the local array. */
  const refreshAddresses = useCallback(async () => {
    if (!customer?.id) return;
    const snap = await getDoc(firestoreDoc(db, 'customers', customer.id));
    if (snap.exists()) setCustomerAddresses((snap.data() as Customer).addresses || []);
  }, [customer?.id]);

  const handleRemoveAddress = (addr: CustomerAddress) => {
    setPendingConfirm({
      kind: 'remove-address',
      addressId: addr.id,
      addressLabel: addr.label || addr.address || 'this address',
      fromService: addr.source === 'service',
    });
  };

  // Dispatcher — called by ConfirmModal.onConfirm(reason?).
  const runConfirm = async (reason?: string) => {
    if (!pendingConfirm || !customer?.id) return;
    setConfirmLoading(true);
    try {
      switch (pendingConfirm.kind) {
        // Service-state changes (suspend / cancel / reactivate) no longer
        // trigger a full loadCustomerData() refetch — services come from
        // the real-time onSnapshot subscription so the status badge +
        // action buttons update on the Firestore push.
        case 'suspend-service':
          await suspendService(pendingConfirm.serviceId, reason || '');
          break;
        case 'cancel-service':
          await cancelService(pendingConfirm.serviceId, reason || '');
          break;
        case 'reactivate-service':
          await reactivateService(pendingConfirm.serviceId);
          break;
        case 'delete-customer': {
          const hasChildren = !!customer.isCompany && childCustomers.length > 0;
          if (hasChildren) {
            await Promise.all(
              childCustomers
                .filter((c) => c.id)
                .map((c) =>
                  linkCustomerToCompany(c.id!, null).catch((e) => {
                    console.error('Failed to unlink child', c.id, e);
                  }),
                ),
            );
          }
          await deleteCustomer(customer.id);
          onCustomerDeleted?.(customer.id);
          setPendingConfirm(null);
          setConfirmLoading(false);
          // If we deleted a navigated-to child, pop back to the level
          // above so the user doesn't lose their seed-customer context.
          // Only fully close when there's nothing to fall back to.
          if (navStack.length > 0) {
            popNav();
          } else {
            onClose();
          }
          return;
        }
        case 'delete-contact':
          await deleteContact(customer.id, pendingConfirm.contactId);
          break;
        case 'remove-address': {
          await removeCustomerAddress(customer.id, pendingConfirm.addressId);
          await refreshAddresses();
          break;
        }
      }
      setPendingConfirm(null);
    } catch (error) {
      console.error('[CustomerDetailModal] Confirm action failed:', error);
      // Keep the modal open so the user can retry. A toast layer is on
      // the Phase 4 follow-up list — for now the console + still-open
      // modal is the failure signal.
    } finally {
      setConfirmLoading(false);
    }
  };

  // Resolve the active pendingConfirm to display props.
  const confirmConfig = useMemo(() => {
    if (!pendingConfirm) return null;
    switch (pendingConfirm.kind) {
      case 'suspend-service':
        return {
          title: `Suspend ${pendingConfirm.serviceName}?`,
          description: 'The service will be marked as suspended. You can reactivate it later — billing pauses until then.',
          confirmLabel: 'Suspend service',
          variant: 'default' as const,
          requireReason: true,
          reasonRequired: true,
          reasonLabel: 'Why are you suspending it?',
          reasonPlaceholder: 'e.g. customer requested pause, awaiting payment...',
        };
      case 'cancel-service':
        return {
          title: `Cancel ${pendingConfirm.serviceName}?`,
          description: 'The service will be permanently cancelled. This cannot be undone.',
          confirmLabel: 'Cancel service',
          variant: 'danger' as const,
          requireReason: true,
          reasonRequired: true,
          reasonLabel: 'Cancellation reason',
          reasonPlaceholder: 'e.g. customer churned, equipment returned...',
        };
      case 'reactivate-service':
        return {
          title: `Reactivate ${pendingConfirm.serviceName}?`,
          description: 'Billing resumes immediately and the service returns to Active.',
          confirmLabel: 'Reactivate',
          variant: 'default' as const,
        };
      case 'delete-customer': {
        const warnings: string[] = [];
        if (deals.length > 0) warnings.push(`• ${deals.length} deal${deals.length === 1 ? '' : 's'} will be orphaned`);
        if (jobs.length > 0) warnings.push(`• ${jobs.length} job${jobs.length === 1 ? '' : 's'} will be orphaned`);
        if (customerFaults.length > 0) warnings.push(`• ${customerFaults.length} fault${customerFaults.length === 1 ? '' : 's'} will be orphaned`);
        if (services.length > 0) warnings.push(`• ${services.length} active service${services.length === 1 ? '' : 's'} will be orphaned`);
        const hasChildren = !!customer?.isCompany && childCustomers.length > 0;
        if (hasChildren) warnings.push(`• ${childCustomers.length} linked site${childCustomers.length === 1 ? '' : 's'} will be unlinked (but kept)`);
        const displayName = customer?.isCompany ? (customer?.companyName || customer?.name) : customer?.name;
        return {
          title: `Delete "${displayName}"?`,
          description: warnings.length > 0
            ? `This customer has linked records:\n${warnings.join('\n')}\n\nThey'll lose their customer reference but remain in the database. This action cannot be undone.`
            : 'This action cannot be undone.',
          confirmLabel: 'Delete customer',
          variant: 'danger' as const,
        };
      }
      case 'delete-contact':
        return {
          title: `Delete contact "${pendingConfirm.contactName}"?`,
          description: 'They will be removed from this customer. This cannot be undone.',
          confirmLabel: 'Delete contact',
          variant: 'danger' as const,
        };
      case 'remove-address':
        return {
          title: `Remove "${pendingConfirm.addressLabel}"?`,
          description: pendingConfirm.fromService
            ? 'The address came from a linked service. Removing it here takes it out of this customer\'s address book for good — the service itself and its own address stay intact. Add it again by hand if you need it back.'
            : 'The address will be removed from this customer for good. Services linked to it stay intact. Add it again by hand if you need it back.',
          confirmLabel: 'Remove address',
          variant: 'danger' as const,
        };
    }
  }, [pendingConfirm, deals.length, jobs.length, customerFaults.length, services.length, childCustomers.length, customer]);

  // Escape is suspended while the Create menu or one of the intake modals it
  // launches is stacked on top. Those children register their own
  // document-level Escape handlers, and document listeners can't
  // stopPropagation each other — without opting out, one Escape press
  // dismisses the child AND this modal underneath it.
  const modalRef = useModalAccessibility({
    isOpen,
    onClose,
    escapeDisabled:
      createMenuOpen || isFaultIntakeOpen || isDealWizardOpen || isServiceRequestIntakeOpen,
  });

  /**
   * Create menu → intake modal. Each intake modal already accepts
   * preSelectedCustomerId, so the operator lands straight on the form.
   */
  const handleCreate = (action: CustomerCreateAction) => {
    if (action === 'deal') setIsDealWizardOpen(true);
    else if (action === 'fault') setIsFaultIntakeOpen(true);
    else setIsServiceRequestIntakeOpen(true);
  };

  /**
   * Shared post-create handler. Faults / deals / service requests are all
   * one-shot loaded by loadCustomerData(), so a refetch is what makes the new
   * record appear without a close-and-reopen. Landing the user on Activity
   * closes the loop visually — they see the thing they just created.
   */
  const handleRecordCreated = () => {
    loadCustomerData();
    setActiveTab('activity');
  };

  // ── Header KPIs (Phase 1) ──────────────────────────────────────────
  // MRR mirrors the convention in CustomerServicesPage:
  //   sum of monthlyPrice across Active services excluding 'one-time'.
  const mrr = useMemo(
    () =>
      services
        .filter((s) => s.status === 'Active' && s.billingType !== 'one-time' && s.monthlyPrice)
        .reduce((sum, s) => sum + (s.monthlyPrice || 0), 0),
    [services],
  );

  // Last-contact: most recent ISO timestamp across call_logs, jobs, faults.
  // Uses createdAt for jobs/faults (the earliest signal of "we touched this
  // customer"), and timestamp for calls. Anything missing the field is skipped.
  const lastContactAt = useMemo(() => {
    const stamps: string[] = [];
    callLogs.forEach((c) => {
      const ts =
        typeof c.timestamp === 'string'
          ? c.timestamp
          : c.timestamp && typeof (c.timestamp as { toDate?: unknown }).toDate === 'function'
          ? (c.timestamp as { toDate(): Date }).toDate().toISOString()
          : '';
      if (ts) stamps.push(ts);
    });
    jobs.forEach((j) => { if (j.createdAt) stamps.push(j.createdAt); });
    customerFaults.forEach((f) => { if (f.createdAt) stamps.push(f.createdAt); });
    if (stamps.length === 0) return null;
    return stamps.sort((a, b) => b.localeCompare(a))[0];
  }, [callLogs, jobs, customerFaults]);

  const lastContactLabel = useMemo(() => {
    if (!lastContactAt) return null;
    const then = new Date(lastContactAt).getTime();
    if (!Number.isFinite(then)) return null;
    const diffMs = Date.now() - then;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }, [lastContactAt]);

  // Primary phone (denormalized at customer level — used by Call / SMS pills).
  const primaryPhone = customer?.phone || customer?.phoneSecondary || '';

  // Xero contact deep-link — only render when xeroContactId is populated.
  const xeroUrl = customer?.xeroContactId
    ? `https://go.xero.com/Contacts/View/${customer.xeroContactId}`
    : null;

  const handleCopyEmail = async () => {
    if (!customer?.email) return;
    try {
      await navigator.clipboard.writeText(customer.email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (!isOpen || !customer) return null;

  return (
    // Slide-over container — right-side panel pattern (Phase 2). The
    // backdrop is lighter than a centered modal so the user can still
    // see the underlying scheduler / list while a customer record is
    // open. Click backdrop or Esc to close (Esc handled by
    // useModalAccessibility).
    <div
      className={`fixed inset-0 ${zIndexClass} bg-black/40 backdrop-blur-[2px]`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Customer Details"
    >
      <div
        ref={modalRef}
        // Theme-aware shell (Phase 4). In dark mode `bg-surface-card`
        // resolves to the deep navy card surface; in light mode it
        // resolves to white. Border + brand-secondary accent stays gold
        // so the modal still reads as a distinct branded surface.
        className="fixed inset-y-0 right-0 w-full max-w-[min(760px,95vw)] bg-surface-card border-l border-theme shadow-2xl flex flex-col"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header — stacked: identity row, at-a-glance strip, quick-action row.
            Each section is conditional, so for a customer with no phone/email
            and no actionable services the header stays compact. */}
        {/* z-20: backdrop-blur makes this a stacking context, and the Create
            menu drops out of it over the tab strip + content region below. */}
        <div className="sticky top-0 z-20 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex flex-col gap-4">
          {/* Identity row — avatar / name / chips / subtitle on the left,
              icon actions (Edit / Delete / Close) on the right. */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 ${
                customer.isCompany
                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                  : 'bg-gradient-to-br from-brand-secondary to-yellow-600'
              }`}>
                {customer.isCompany
                  ? <Building2 className="w-6 h-6 text-white" />
                  : <User className="w-6 h-6 text-white" />}
              </div>
              <div className="min-w-0 flex-1">
                {/* Parent company breadcrumb — clickable (Phase 5) so the
                    user can jump up to the parent company without closing
                    the modal. Falls back to a static label when there's
                    no parent. */}
                {customer.parentCompanyId && customer.parentCompanyName && (
                  <button
                    type="button"
                    onClick={() => navigateToCustomer(customer.parentCompanyId!)}
                    className="flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200 hover:underline mb-0.5 -ml-0.5 px-1 py-0.5 rounded transition-colors"
                    title={`Open ${customer.parentCompanyName}`}
                  >
                    <Building2 className="w-3 h-3" />
                    <span className="truncate">{customer.parentCompanyName}</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-2xl font-bold text-white truncate">
                    {customer.isCompany ? (customer.companyName || customer.name) : customer.name}
                  </h2>
                  {customer.isCompany && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      <Building2 className="w-3 h-3" />
                      Company
                    </span>
                  )}
                  {customer.isVip && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/30 rounded-full text-xs font-bold">
                      <Star className="w-3 h-3 fill-current" />
                      VIP
                    </span>
                  )}
                  {/* Xero deep-link pill — only renders for customers that have
                      been pushed to Xero (xeroContactId is set on first invoice). */}
                  {xeroUrl && (
                    <a
                      href={xeroUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-500/15 text-sky-300 border border-sky-500/30 rounded-full text-[10px] font-bold uppercase tracking-wider hover:bg-sky-500/25 transition-colors"
                      title="Open in Xero"
                    >
                      Xero
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-white/60 flex-wrap mt-0.5">
                  {customer.accountNumber && (
                    <button
                      onClick={handleCopyAccountNumber}
                      className="group inline-flex items-center gap-1.5 font-mono text-xs text-white/85 hover:text-white transition-colors"
                      title="Copy account number"
                    >
                      <span>{customer.accountNumber}</span>
                      {copiedAccountNumber
                        ? <Check className="w-3 h-3 text-emerald-400" />
                        : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </button>
                  )}
                  {customer.isCompany && customer.tradingName && (
                    <span>t/a {customer.tradingName}</span>
                  )}
                  {customer.isCompany && customer.abn && (
                    <span className="font-mono text-xs">ABN {customer.abn}</span>
                  )}
                  {customer.propertyType && <span>{customer.propertyType}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setIsEditOpen(true)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Edit Customer"
              >
                <Edit2 className="w-5 h-5 text-white/60 hover:text-brand-secondary" />
              </button>
              <button
                onClick={handleDeleteCustomer}
                disabled={confirmLoading && pendingConfirm?.kind === 'delete-customer'}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Delete Customer"
              >
                <Trash2 className="w-5 h-5 text-white/60 hover:text-red-400" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          {/* At-a-glance strip — the four data points the office most often
              opens this modal to read: how to reach them, how much they pay,
              and how recently we touched them. Each pill is independently
              conditional so we don't render empty sockets. */}
          {(primaryPhone || customer.email || mrr > 0 || lastContactLabel) && (
            <div className="flex items-center gap-2 flex-wrap text-sm">
              {primaryPhone && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/90">
                  <Phone className="w-3.5 h-3.5 text-brand-secondary" />
                  <PhoneNumber phone={primaryPhone} numberClassName="text-white/90" />
                </span>
              )}
              {customer.email && (
                <button
                  onClick={handleCopyEmail}
                  className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 transition-colors"
                  title="Copy email"
                >
                  <Mail className="w-3.5 h-3.5 text-brand-secondary" />
                  <span className="truncate max-w-[220px]">{customer.email}</span>
                  {copiedEmail
                    ? <Check className="w-3 h-3 text-emerald-400" />
                    : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-white/60" />}
                </button>
              )}
              {mrr > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-semibold"
                  title="Monthly recurring revenue across active services"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  {`${formatCurrency(mrr, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/mo`}
                </span>
              )}
              {lastContactLabel && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/85"
                  title={`Last contact: ${lastContactAt ? formatDate(lastContactAt) : ''}`}
                >
                  <Clock className="w-3.5 h-3.5 text-white/60" />
                  Last contact: <span className="font-semibold text-white">{lastContactLabel}</span>
                </span>
              )}
            </div>
          )}

          {/* Quick-action row — reach-the-customer actions on the left, the
              create-a-record menu pinned right. Call / SMS / Email use native
              handlers (tel:, sms:, mailto:); Log Call writes to call_logs.
              Deal / Fault / Service request used to sit inline here as
              separate pills — they now live behind the one "New" menu
              (CustomerCreateMenu) so the row stays on a single line and the
              three record types are described rather than guessed at. */}
          <div className="flex items-center gap-2 flex-wrap">
            {primaryPhone && (
              <>
                <a
                  href={`tel:${primaryPhone}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-xs font-semibold transition-colors"
                  title={`Call ${primaryPhone}`}
                >
                  <Phone className="w-3.5 h-3.5" />
                  Call
                </a>
                <a
                  href={`sms:${primaryPhone}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/30 text-xs font-semibold transition-colors"
                  title={`Text ${primaryPhone}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  SMS
                </a>
              </>
            )}
            {customer.email && (
              <a
                href={`mailto:${customer.email}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 border border-purple-500/30 text-xs font-semibold transition-colors"
                title={`Email ${customer.email}`}
              >
                <Mail className="w-3.5 h-3.5" />
                Email
              </a>
            )}
            <button
              onClick={() => setIsLogCallOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 border border-sky-500/30 text-xs font-semibold transition-colors"
              title="Log a call against this customer"
            >
              <Phone className="w-3.5 h-3.5" />
              Log Call
            </button>
            <div className="ml-auto">
              <CustomerCreateMenu
                onSelect={handleCreate}
                onOpenChange={setCreateMenuOpen}
                customerName={
                  customer.isCompany ? customer.companyName || customer.name : customer.name
                }
              />
            </div>
          </div>
        </div>

        {/* Nav breadcrumb (Phase 5) — when the user has drilled into a
            child site or up to a parent, surface a back affordance
            showing the previous level. Tap to pop one level; clearing
            the whole stack happens automatically on modal close +
            seedCustomer change. */}
        {navStack.length > 0 && (
          <div className="bg-white/5 border-b border-white/10 px-6 py-2 flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={popNav}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 text-white/85 hover:text-white font-semibold transition-colors"
              title={`Back to ${navStack[navStack.length - 1].name || 'previous'}`}
            >
              <ChevronRight className="w-3 h-3 rotate-180" />
              Back to {navStack[navStack.length - 1].name || 'previous'}
            </button>
            <span className="text-white/40">
              {navStack.length === 1 ? '1 level deep' : `${navStack.length} levels deep`}
            </span>
          </div>
        )}

        {/* Merged-customer banner — surface immediately under the header so
            anyone landing on a redirect stub knows this record is archival.
            Doesn't block reads, but should never be edited from. */}
        {customer.mergedInto && (
          <div className="bg-amber-500/15 border-b border-amber-500/30 px-6 py-3 flex items-start gap-3 text-sm">
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-amber-200 font-semibold">This customer was merged.</p>
              <p className="text-amber-200/80 text-xs mt-0.5">
                Data on this record is read-only history.
                {customer.mergedAt && ` Merged ${formatDate(customer.mergedAt)}`}
                {customer.mergedByName && ` by ${customer.mergedByName}`}.
                The active record is <span className="font-mono">{customer.mergedInto.slice(0, 12)}…</span>
              </p>
            </div>
          </div>
        )}

        {/* Tabs — 4 lenses on the customer record. Information is the
            default landing tab (key facts + sites + addresses + vault).
            Activity is the unified timeline (replaces the old Calls /
            Jobs / Deals / Faults / History tabs). Services owns the
            actionable subscription lifecycle. Files aggregates attached
            assets across linked entities. ARIA tablist semantics applied. */}
        <div className="border-b border-white/10 px-6">
          <div role="tablist" aria-label="Customer record sections" className="flex gap-4">
            {([
              { id: 'info',     label: 'Information', icon: User,      badge: null,                                                                              shortcut: '⌘1' },
              { id: 'activity', label: 'Activity',    icon: Clock,     badge: deals.length + jobs.length + customerFaults.length + callLogs.length + serviceRequests.length + customerNotes.length, shortcut: '⌘2' },
              { id: 'services', label: 'Services',    icon: Wifi,      badge: services.length || null,                                              shortcut: '⌘3' },
              { id: 'files',    label: 'Files',       icon: Paperclip, badge: customer.attachments?.length || null,                                shortcut: '⌘4' },
            ] as const).filter((tab) => tab.id !== 'services' || hasCustomerServices).map((tab) => {
              const active = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`customer-tab-${tab.id}`}
                  aria-selected={active}
                  aria-controls={`customer-tabpanel-${tab.id}`}
                  tabIndex={active ? 0 : -1}
                  title={`${tab.label} (${tab.shortcut})`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-1 py-3 font-semibold border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/40 rounded-t-sm ${
                    active
                      ? 'border-brand-secondary text-brand-secondary'
                      : 'border-transparent text-white/60 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    {tab.badge !== null && tab.badge > 0 && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/15 text-white/85 tabular-nums">
                        {tab.badge}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            // Skeleton loader (Phase 3) — three placeholder cards that
            // roughly match the Information tab's card hierarchy
            // (Main Contact, Alternative Contacts, Addresses). Cheaper
            // than the previous "Loading…" text and reduces layout shift
            // when the real cards swap in.
            <div className="space-y-6" aria-busy="true" aria-live="polite">
              <span className="sr-only">Loading customer details…</span>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-lg border border-white/10 p-6 space-y-3"
                >
                  <div className="h-5 w-40 bg-white/10 rounded animate-pulse" />
                  <div className="h-4 w-full bg-white/5 rounded animate-pulse" />
                  <div className="h-4 w-3/4 bg-white/5 rounded animate-pulse" />
                  <div className="h-4 w-1/2 bg-white/5 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Info Tab */}
              {activeTab === 'info' && (
                <div
                  role="tabpanel"
                  id="customer-tabpanel-info"
                  aria-labelledby="customer-tab-info"
                  className="space-y-6"
                >
                  {/* Customer 360 roll-ups (Day 4): signed proposals + open
                      invoices derived from the customer's deals. Each card
                      renders a skeleton while `aggregatesLoading` is true, so
                      the user never confuses "loading" with "no proposals".
                      Fix-pass: also treat `aggregates === null` as loading so
                      the one-render-frame between a customer-state change and
                      the useEffect-driven `setAggregatesLoading(true)` doesn't
                      flash the previous customer's data. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ProposalsCard
                      proposals={aggregates?.signedProposals ?? []}
                      loading={aggregatesLoading || !aggregates}
                    />
                    <InvoicesCard
                      summary={
                        aggregates?.invoiceSummary ?? {
                          outstandingTotal: 0,
                          openCount: 0,
                          badDebtorCount: 0,
                        }
                      }
                      loading={aggregatesLoading || !aggregates}
                      customerId={customer.id ?? null}
                    />
                  </div>

                  {/* Main Contact Information */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Main Contact Information</h3>
                    <div className="space-y-3">
                      {customer.isCompany && customer.primaryContactName && (
                        <div className="flex items-center gap-3 text-white/85">
                          <User className="w-5 h-5 text-brand-secondary" />
                          <span>{customer.primaryContactName}</span>
                          <span className="text-xs text-white/40">(Primary contact)</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-3 text-white/85">
                          <Phone className="w-5 h-5 text-brand-secondary" />
                          <PhoneNumber phone={customer.phone} />
                        </div>
                      )}
                      {customer.phoneSecondary && (
                        <div className="flex items-center gap-3 text-white/85">
                          <Phone className="w-5 h-5 text-brand-secondary" />
                          <PhoneNumber
                            phone={customer.phoneSecondary}
                            suffix={<span className="text-xs text-white/40">(Secondary)</span>}
                          />
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center gap-3 text-white/85">
                          <Mail className="w-5 h-5 text-brand-secondary" />
                          <span>{customer.email}</span>
                        </div>
                      )}
                      {!customer.phone && !customer.phoneSecondary && !customer.email && !(customer.isCompany && customer.primaryContactName) && (
                        <div className="text-white/60 text-sm">No main contact information on file</div>
                      )}
                    </div>
                  </div>

                  {/* Alternative Contacts */}
                  <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white">Alternative Contacts</h3>
                      <button
                        onClick={handleAddContact}
                        className="flex items-center gap-2 px-3 py-2 bg-brand-secondary hover:brightness-110 text-white font-semibold rounded-lg transition-colors text-sm"
                      >
                        <UserPlus className="w-4 h-4" />
                        Add Contact
                      </button>
                    </div>

                    {contacts.length === 0 ? (
                      <div className="text-center py-8 text-white/60">
                        <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="mb-2">No alternative contacts added yet</p>
                        <p className="text-sm text-white/40">
                          Add multiple contacts for this customer (e.g., manager, spouse, IT contact)
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {contacts.map((contact: Contact) => {
                          const isAccount = contactHasRole(contact, 'account');
                          const isSite = contactHasRole(contact, 'site');
                          const primaryAccount = !!contact.isPrimaryAccount;
                          const primarySite = !!contact.isPrimarySite;
                          // Legacy primary — surface as "both" when no explicit flags are set
                          const hasExplicit =
                            (contact.contactType && contact.contactType.length > 0) ||
                            contact.isPrimaryAccount !== undefined ||
                            contact.isPrimarySite !== undefined;
                          const legacyPrimary = !hasExplicit && !!contact.isPrimary;
                          return (
                            <div
                              key={contact.id}
                              className="bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 p-4 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 space-y-2 min-w-0">
                                  {/* Name + role pills */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="text-white font-semibold">{contact.name}</h4>
                                    {isAccount && (
                                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                        primaryAccount || legacyPrimary
                                          ? 'bg-blue-500/25 text-blue-200 border-blue-300/40'
                                          : 'bg-blue-500/10 text-blue-300 border-blue-500/20'
                                      }`}>
                                        <Briefcase className="w-3 h-3" />
                                        {primaryAccount || legacyPrimary ? 'Primary Account' : 'Account'}
                                      </span>
                                    )}
                                    {isSite && (
                                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                        primarySite || legacyPrimary
                                          ? 'bg-emerald-500/25 text-emerald-200 border-emerald-300/40'
                                          : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                                      }`}>
                                        <MapPin className="w-3 h-3" />
                                        {primarySite || legacyPrimary ? 'Primary Site' : 'Site'}
                                      </span>
                                    )}
                                    {contact.role && (
                                      <span className="px-2 py-0.5 bg-white/10 text-white/85 rounded-full text-[10px] border border-white/10">
                                        {contact.role}
                                      </span>
                                    )}
                                  </div>

                                  {/* Contact Details */}
                                  <div className="space-y-1 text-sm">
                                    {contact.phone && (
                                      <div className="flex items-center gap-2 text-white/85">
                                        <Phone className="w-4 h-4 text-brand-secondary" />
                                        <PhoneNumber
                                          phone={contact.phone}
                                          numberClassName="hover:text-brand-secondary transition-colors"
                                        />
                                      </div>
                                    )}
                                    {contact.email && (
                                      <div className="flex items-center gap-2 text-white/85">
                                        <Mail className="w-4 h-4 text-brand-secondary" />
                                        <a href={`mailto:${contact.email}`} className="hover:text-brand-secondary transition-colors">
                                          {contact.email}
                                        </a>
                                      </div>
                                    )}
                                    {contact.addressIds && contact.addressIds.length > 0 && customerAddresses.length > 0 && (
                                      <div className="flex items-start gap-2 text-white/60 text-xs pt-1">
                                        <Home className="w-3.5 h-3.5 mt-0.5 text-emerald-400/70 flex-shrink-0" />
                                        <div className="flex flex-wrap gap-1">
                                          {contact.addressIds.map((addrId) => {
                                            const addr = customerAddresses.find((a) => a.id === addrId);
                                            return addr ? (
                                              <span key={addrId} className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-300 rounded text-[10px] border border-emerald-500/20">
                                                {addr.label}
                                              </span>
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    )}
                                    {contact.notes && (
                                      <p className="text-white/60 text-xs mt-2">{contact.notes}</p>
                                    )}
                                  </div>
                                </div>

                                {/* Actions — dual primary stars + edit/delete */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {isAccount && !primaryAccount && !legacyPrimary && (
                                    <button
                                      onClick={async () => {
                                        if (!customer?.id || !contact.id) return;
                                        await setPrimaryAccountContact(customer.id, contact.id);
                                      }}
                                      className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors"
                                      title="Set as primary account contact"
                                    >
                                      <Briefcase className="w-4 h-4 text-white/60 hover:text-blue-400" />
                                    </button>
                                  )}
                                  {isSite && !primarySite && !legacyPrimary && (
                                    <button
                                      onClick={async () => {
                                        if (!customer?.id || !contact.id) return;
                                        await setPrimarySiteContact(customer.id, contact.id);
                                      }}
                                      className="p-2 hover:bg-emerald-500/20 rounded-lg transition-colors"
                                      title="Set as primary site contact"
                                    >
                                      <MapPin className="w-4 h-4 text-white/60 hover:text-emerald-400" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditContact(contact)}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                    title="Edit contact"
                                  >
                                    <Edit2 className="w-4 h-4 text-white/60 hover:text-white" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteContact(contact)}
                                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                                    title="Delete contact"
                                  >
                                    <Trash2 className="w-4 h-4 text-white/60 hover:text-red-400" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Addresses — add / edit / tag / remove (CustomerAddressBook) */}
                  <CustomerAddressBook
                    customer={customer}
                    addresses={customerAddresses}
                    onChanged={refreshAddresses}
                    onRequestRemove={handleRemoveAddress}
                  />

                  {/* Access Instructions & Site Notes */}
                  {(customer.accessInstructions || customer.siteNotes) && (
                    <div className="bg-amber-500/5 rounded-lg border border-amber-500/20 p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        Site Access & Notes
                      </h3>
                      <div className="space-y-3">
                        {customer.accessInstructions && (
                          <div>
                            <div className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1">Access Instructions</div>
                            <p className="text-white/85 whitespace-pre-wrap">{customer.accessInstructions}</p>
                          </div>
                        )}
                        {customer.siteNotes && (
                          <div>
                            <div className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1">Site Notes</div>
                            <p className="text-white/85 whitespace-pre-wrap">{customer.siteNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Site Vault — office-managed customer/site passwords
                      (WiFi, gate / alarm codes, router admin, portal logins).
                      Editable here; techs see a read-only, tap-to-reveal copy
                      on their job card. Always rendered so office can populate
                      it from scratch. */}
                  <SiteVaultCard
                    customer={customer}
                    jobs={jobs}
                    onSaved={(updated) => {
                      setCustomer(updated);
                      if (onCustomerUpdated) onCustomerUpdated(updated);
                    }}
                  />

                  {/* General Notes */}
                  {customer.notes && (
                    <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                      <h3 className="text-lg font-bold text-white mb-4">Notes</h3>
                      <p className="text-white/85 whitespace-pre-wrap">{customer.notes}</p>
                    </div>
                  )}

                  {/* Sites — companies only. Folded into Information (Phase 2)
                      so company → linked-site context is visible alongside the
                      contacts / addresses / vault rather than buried behind
                      its own tab. */}
                  {customer.isCompany && (
                    <div className="bg-white/5 rounded-lg border border-white/10 p-6">
                      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-brand-secondary" />
                          Linked Sites
                          {childCustomers.length > 0 && (
                            <span className="text-sm font-normal text-white/60">({childCustomers.length})</span>
                          )}
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setIsLinkExistingOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors text-sm border border-white/10"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Link Existing
                          </button>
                          <button
                            onClick={() => setIsAddSiteOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-secondary hover:brightness-110 text-white font-semibold rounded-lg transition-colors text-sm"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add New Site
                          </button>
                        </div>
                      </div>
                      {childCustomers.length === 0 ? (
                        <div className="text-center py-6 text-white/60 bg-black/10 rounded-lg border border-white/5">
                          <Building2 className="w-8 h-8 mx-auto mb-2 text-white/40 opacity-70" />
                          <p className="text-sm">No sites linked yet</p>
                          <p className="text-xs text-white/40 mt-1">
                            Link an existing customer as a site, or create a brand-new one under this company.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {childCustomers.map((child) => (
                            <button
                              type="button"
                              key={child.id}
                              onClick={() => child.id && navigateToCustomer(child.id)}
                              className="w-full text-left flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors group"
                              title={`Open ${child.name}`}
                            >
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-secondary/40 to-yellow-600/40 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-white/80" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-white font-semibold truncate text-sm">{child.name}</h4>
                                  {child.accountNumber && (
                                    <span className="font-mono text-[10px] text-white/60">{child.accountNumber}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-white/60 mt-0.5">
                                  {child.email && <span className="truncate">{child.email}</span>}
                                  {child.phone && <PhoneNumber phone={child.phone} hideCopyUntilHover />}
                                  {child.city && <span>{child.city}</span>}
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white/85 shrink-0 transition-colors" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Calls / Jobs / Deals / Faults dedicated tabs removed
                  (Phase 2) — folded into the Activity tab below. */}

              {/* Services Tab */}
              {activeTab === 'services' && (
                <div
                  role="tabpanel"
                  id="customer-tabpanel-services"
                  aria-labelledby="customer-tab-services"
                  className="space-y-4"
                >
                  {/* MRR Summary */}
                  {services.some((s) => s.status === 'Active' && s.monthlyPrice) && (
                    <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white/60">Monthly Recurring Revenue</div>
                          <div className="text-2xl font-bold text-green-400">
                            ${services
                              .filter((s) => s.status === 'Active' && s.monthlyPrice)
                              .reduce((sum: number, s) => sum + s.monthlyPrice!, 0)
                              .toFixed(2)}/mo
                          </div>
                        </div>
                        <div className="text-sm text-white/60">
                          {services.filter((s) => s.status === 'Active').length} active service
                          {services.filter((s) => s.status === 'Active').length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add Service Button */}
                  <button
                    onClick={() => setIsAddServiceModalOpen(true)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-brand-secondary/20 to-yellow-600/20 hover:from-brand-secondary/30 hover:to-yellow-600/30 border border-brand-secondary/30 text-brand-secondary font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Wifi className="w-5 h-5" />
                    Add Service
                  </button>

                  {services.length === 0 ? (
                    <div className="text-center py-12 text-white/60">
                      <Wifi className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No services found</p>
                      <p className="text-sm mt-2">Click "Add Service" to add a service for this customer</p>
                    </div>
                  ) : (
                    services.map((service) => {
                      const servicePlan = service.planId ? servicePlansById.get(service.planId) : undefined;
                      const serviceSpeed = resolveServiceSpeed(servicePlan, service.metadata);
                      return (
                      <div key={service.id} className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
                        {/* Service Header */}
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-lg font-semibold text-white">{service.serviceType}</h4>
                              {service.monthlyPrice && (
                                <span className="text-brand-secondary font-bold">{formatCurrency(service.monthlyPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</span>
                              )}
                              {/* Speed beside the plan — "NBN Home Fast" alone
                                  doesn't say what the customer actually pays for. */}
                              {serviceSpeed && (
                                <span
                                  className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-sky-500/20 text-sky-300 border border-sky-500/30 whitespace-nowrap"
                                  title={SPEED_SOURCE_TITLE[serviceSpeed.source]}
                                >
                                  {serviceSpeed.label}
                                </span>
                              )}
                            </div>
                            {service.planName && (
                              <p className="text-sm text-white/60">{service.planName}</p>
                            )}
                            {service.metadata?.planName && !service.planName && (
                              <p className="text-sm text-white/60">{service.metadata.planName}</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                            service.status === 'Active' ? 'bg-green-500/20 text-green-400' :
                            service.status === 'Inactive' ? 'bg-white/10 text-white/60' :
                            service.status === 'Pending Install' ? 'bg-yellow-500/20 text-yellow-400' :
                            service.status === 'Suspended' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {service.status}
                          </span>
                        </div>

                        {/* Service Actions (Phase 2 — moved to top of card so
                            Suspend / Cancel / Reactivate are reachable without
                            scrolling past 500px of metadata / linked entities.
                            Edit is always available; lifecycle actions render
                            conditionally based on status. */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditServiceTarget(service)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 font-semibold rounded-lg transition-all text-xs"
                            title="Edit service details (kit number, serials, plan, etc.)"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          {service.status === 'Active' && (
                            <>
                              <button
                                onClick={() => handleSuspendService(service)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 text-orange-300 font-semibold rounded-lg transition-all text-xs"
                              >
                                Suspend
                              </button>
                              <button
                                onClick={() => handleCancelService(service)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 font-semibold rounded-lg transition-all text-xs"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {service.status === 'Suspended' && (
                            <>
                              <button
                                onClick={() => handleReactivateService(service)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/15 hover:bg-green-500/25 border border-green-500/30 text-green-300 font-semibold rounded-lg transition-all text-xs"
                              >
                                Reactivate
                              </button>
                              <button
                                onClick={() => handleCancelService(service)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 font-semibold rounded-lg transition-all text-xs"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </div>

                        {/* Service Address */}
                        {(service.address || service.city || service.state || service.zipCode) && (
                          <div className="flex items-start gap-2 text-sm text-white/85">
                            <MapPin className="w-4 h-4 mt-0.5 text-brand-secondary flex-shrink-0" />
                            <div className="flex-1">
                              {service.address && <div className="font-semibold text-white">{service.address}</div>}
                              {(service.city || service.state || service.zipCode) && (
                                <div className="text-white/60">
                                  {[service.city, service.state, service.zipCode].filter(Boolean).join(' ')}
                                </div>
                              )}
                              {service.addressBound && (
                                <div className="text-xs text-white/40 mt-0.5">Tied to this address</div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Billing Information */}
                        {(service.monthlyPrice || service.billingCycle || service.nextBillingDate || service.activatedAt || service.dealId) && (
                          <div className="bg-black/20 rounded-lg border border-white/5 p-3">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              {service.activatedAt && (
                                <div className="text-white/85">
                                  <span className="text-white/40">Active since:</span>
                                  <div className="font-semibold">{new Date(service.activatedAt).toLocaleDateString(getLocaleSync(), {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}</div>
                                </div>
                              )}
                              {service.nextBillingDate && (
                                <div className="text-white/85">
                                  <span className="text-white/40">Next billing:</span>
                                  <div className="font-semibold">{new Date(service.nextBillingDate).toLocaleDateString(getLocaleSync(), {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}</div>
                                </div>
                              )}
                              {service.billingCycle && (
                                <div className="text-white/85">
                                  <span className="text-white/40">Billing cycle:</span>
                                  <div className="font-semibold capitalize">{service.billingCycle}</div>
                                </div>
                              )}
                              {service.dealId && (
                                <div className="text-white/85">
                                  <span className="text-white/40">From deal:</span>
                                  <div className="font-semibold text-brand-secondary truncate" title={service.dealId}>
                                    #{service.dealId.slice(0, 12)}...
                                  </div>
                                </div>
                              )}
                              {service.accountNumber && (
                                <div className="text-white/85">
                                  <span className="text-white/40">Account #:</span>
                                  <div className="font-semibold">{service.accountNumber}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Service Details Grid */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {/* Starlink Metadata */}
                          {service.serviceType === 'Starlink' && service.metadata?.kitNumber && (
                            <div className="text-white/85">
                              <span className="text-white/40">Kit Number:</span>
                              <div className="font-semibold text-brand-secondary">{service.metadata.kitNumber}</div>
                            </div>
                          )}
                          {service.serviceType === 'Starlink' && service.metadata?.starlinkAccountNumber && (
                            <div className="text-white/85">
                              <span className="text-white/40">Account Number:</span>
                              <div className="font-semibold font-mono">{service.metadata.starlinkAccountNumber}</div>
                            </div>
                          )}
                          {service.serviceType === 'Starlink' && service.metadata?.starlinkAccountEmail && (
                            <div className="text-white/85">
                              <span className="text-white/40">Account Email:</span>
                              <div className="font-semibold break-all">{service.metadata.starlinkAccountEmail}</div>
                            </div>
                          )}

                          {/* NBN Metadata */}
                          {service.serviceType === 'NBN' && service.metadata?.nbnServiceId && (
                            <div className="text-white/85">
                              <span className="text-white/40">NBN Service ID:</span>
                              <div className="font-semibold text-brand-secondary">{service.metadata.nbnServiceId}</div>
                            </div>
                          )}
                          {service.serviceType === 'NBN' && service.metadata?.technology && (
                            <div className="text-white/85">
                              <span className="text-white/40">Technology:</span>
                              <div className="font-semibold">{service.metadata.technology}</div>
                            </div>
                          )}

                          {/* 4G/5G Metadata */}
                          {service.serviceType === '4G/5G' && service.metadata?.carrier && (
                            <div className="text-white/85">
                              <span className="text-white/40">Carrier:</span>
                              <div className={`font-semibold ${service.metadata.carrier === 'Telstra' ? 'text-blue-400' : 'text-teal-400'}`}>
                                {service.metadata.carrier}
                              </div>
                            </div>
                          )}
                          {service.serviceType === '4G/5G' && service.metadata?.simNumber && (
                            <div className="text-white/85">
                              <span className="text-white/40">SIM Number:</span>
                              <div className="font-semibold text-brand-secondary">{service.metadata.simNumber}</div>
                            </div>
                          )}

                          {/* WiFi Extension Metadata */}
                          {service.serviceType === 'WiFi Extension' && service.metadata?.accessPointCount && (
                            <div className="text-white/85">
                              <span className="text-white/40">Access Points:</span>
                              <div className="font-semibold">{service.metadata.accessPointCount}</div>
                            </div>
                          )}

                          {/* Smart Home Metadata */}
                          {service.serviceType === 'Smart Home' && service.metadata?.deviceCount && (
                            <div className="text-white/85">
                              <span className="text-white/40">Devices:</span>
                              <div className="font-semibold">{service.metadata.deviceCount}</div>
                            </div>
                          )}
                          {service.serviceType === 'Smart Home' && service.metadata?.hubSerialNumber && (
                            <div className="text-white/85">
                              <span className="text-white/40">Hub Serial:</span>
                              <div className="font-semibold">{service.metadata.hubSerialNumber}</div>
                            </div>
                          )}

                          {/* Security Metadata */}
                          {service.serviceType === 'Security' && service.metadata?.panelSerialNumber && (
                            <div className="text-white/85">
                              <span className="text-white/40">Panel Serial:</span>
                              <div className="font-semibold text-brand-secondary">{service.metadata.panelSerialNumber}</div>
                            </div>
                          )}
                          {service.serviceType === 'Security' && (service.metadata?.cameraCount || service.metadata?.sensorCount) && (
                            <div className="text-white/85">
                              <span className="text-white/40">Equipment:</span>
                              <div className="font-semibold">
                                {service.metadata.cameraCount ? `${service.metadata.cameraCount} cameras` : ''}
                                {service.metadata.cameraCount && service.metadata.sensorCount ? ', ' : ''}
                                {service.metadata.sensorCount ? `${service.metadata.sensorCount} sensors` : ''}
                              </div>
                            </div>
                          )}

                          {/* Common Metadata */}
                          {service.metadata?.accountNumber && (
                            <div className="text-white/85">
                              <span className="text-white/40">Account #:</span>
                              <div className="font-semibold">{service.metadata.accountNumber}</div>
                            </div>
                          )}
                          {service.metadata?.monthlyFee && (
                            <div className="text-white/85">
                              <span className="text-white/40">Monthly Fee:</span>
                              <div className="font-semibold">${service.metadata.monthlyFee}</div>
                            </div>
                          )}
                        </div>

                        {/* Service Notes */}
                        {service.notes && (
                          <div className="text-white/85 text-sm border-t border-white/5 pt-3">
                            <div className="font-semibold text-white mb-1">Notes:</div>
                            <p className="whitespace-pre-wrap">{service.notes}</p>
                          </div>
                        )}

                        {/* Service-Linked Calls */}
                        {(() => {
                          const serviceCalls = callLogs.filter((call) => call.serviceId === service.id);
                          if (serviceCalls.length > 0) {
                            return (
                              <div className="bg-black/20 rounded-lg border border-white/5 p-3 space-y-2">
                                <div className="font-semibold text-white text-sm flex items-center gap-2">
                                  <Phone className="w-4 h-4 text-brand-secondary" />
                                  Calls for This Service ({serviceCalls.length})
                                </div>
                                <div className="space-y-2">
                                  {serviceCalls.slice(0, 3).map((call) => (
                                    <div
                                      key={call.id}
                                      onClick={() => setSelectedCall(call)}
                                      className="bg-white/5 hover:bg-white/10 rounded p-2 cursor-pointer transition-all text-xs"
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getCallTypeColor(call.callType)}`}>
                                          {call.callType}
                                        </span>
                                        <span className="text-white/40">{formatDate(call.timestamp)}</span>
                                      </div>
                                      <p className="text-white/85 line-clamp-2">{call.notes}</p>
                                    </div>
                                  ))}
                                  {serviceCalls.length > 3 && (
                                    <p className="text-xs text-white/40 text-center">
                                      +{serviceCalls.length - 3} more calls (view in Calls tab)
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Service-Linked Jobs */}
                        {(() => {
                          const serviceJobs = jobs.filter((job: Job & { serviceId?: string }) => job.serviceId === service.id);
                          if (serviceJobs.length > 0) {
                            return (
                              <div className="bg-black/20 rounded-lg border border-white/5 p-3 space-y-2">
                                <div className="font-semibold text-white text-sm flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-brand-secondary" />
                                  Jobs for This Service ({serviceJobs.length})
                                </div>
                                <div className="space-y-2">
                                  {serviceJobs.slice(0, 3).map((job) => (
                                    <div
                                      key={job.id}
                                      onClick={() => setSelectedJob(job)}
                                      className="bg-white/5 hover:bg-white/10 rounded p-2 cursor-pointer transition-all text-xs"
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-white font-semibold">{job.type}</span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(job.status)}`}>
                                          {job.status}
                                        </span>
                                      </div>
                                      {job.date && (
                                        <div className="flex items-center gap-1 text-white/40 mb-1">
                                          <Calendar className="w-3 h-3" />
                                          {job.date}
                                        </div>
                                      )}
                                      {job.notes && (
                                        <p className="text-white/85 line-clamp-2">{job.notes}</p>
                                      )}
                                    </div>
                                  ))}
                                  {serviceJobs.length > 3 && (
                                    <p className="text-xs text-white/40 text-center">
                                      +{serviceJobs.length - 3} more jobs (view in Jobs tab)
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Service-Linked Faults — derived from the already-
                            loaded customerFaults; no separate fetch. */}
                        {(() => {
                          const faults = service.id ? (serviceFaults[service.id] || []) : [];
                          if (faults.length > 0) {
                            return (
                              <div className="bg-black/20 rounded-lg border border-rose-500/10 p-3 space-y-2">
                                <div className="font-semibold text-white text-sm flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-rose-400" />
                                  Fault History ({faults.length})
                                  {faults.filter((f: Fault) => !f.archived).length > 0 && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                      {faults.filter((f: Fault) => !f.archived).length} open
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  {faults.slice(0, 5).map((fault: Fault) => (
                                    <button
                                      type="button"
                                      key={fault.id}
                                      onClick={() => setSelectedFault(fault)}
                                      className="w-full text-left bg-white/5 rounded p-2 text-xs hover:bg-white/10 transition-colors cursor-pointer"
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                          <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                                            fault.severity === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                            fault.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                                            fault.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                            'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                          }`}>
                                            {fault.severity}
                                          </span>
                                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                            fault.archived ? 'bg-white/10 text-white/60' : 'bg-emerald-500/20 text-emerald-400'
                                          }`}>
                                            {fault.archived ? (fault.resolutionType === 'deployed' ? 'Deployed' : 'Resolved') : fault.stage}
                                          </span>
                                        </div>
                                        <span className="text-white/40">{formatDate(fault.createdAt)}</span>
                                      </div>
                                      <p className="text-white/85 line-clamp-2">{fault.symptom}</p>
                                      {fault.costs && fault.costs.totalCost > 0 && (
                                        <div className="text-white/40 mt-1">
                                          Cost: <span className="text-emerald-400">{formatCurrency(fault.costs.totalCost, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </div>
                                      )}
                                    </button>
                                  ))}
                                  {faults.length > 5 && (
                                    <p className="text-xs text-white/40 text-center">
                                      +{faults.length - 5} more faults
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Service Footer */}
                        <div className="flex items-center justify-between text-xs text-white/40 pt-2 border-t border-white/5">
                          <div className="flex items-center gap-2">
                            {service.installedAt && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Installed {formatDate(service.installedAt)}
                              </div>
                            )}
                            {service.installedByName && (
                              <span>by {service.installedByName}</span>
                            )}
                          </div>
                          {service.metadata?.contractEndDate && (
                            <span className="text-yellow-400">Contract ends: {service.metadata.contractEndDate}</span>
                          )}
                        </div>

                        {/* Status-specific footer hints (moved out of the
                            actions row — these are informational, not
                            actionable). */}
                        {service.status === 'Pending Install' && (
                          <div className="text-sm text-white/60 text-center pt-3 border-t border-white/5">
                            Service will be activated upon installation completion
                          </div>
                        )}
                        {(service.status === 'Cancelled' || service.status === 'Inactive') && (service.cancellationReason || service.deactivationReason) && (
                          <div className="text-sm text-white/40 pt-3 border-t border-white/5">
                            <span className="text-white/60 font-semibold">Reason: </span>
                            {service.cancellationReason || service.deactivationReason}
                          </div>
                        )}
                      </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Activity Tab (Phase 2) — unified timeline replacing the
                  separate Calls / Jobs / Deals / Faults / History tabs.
                  Filter chips along the top + search box. Rows are
                  clickable: calls / jobs / faults open their detail modals
                  (CallDetailModal, JobDetailModal, FaultDetailsView) and
                  deals navigate to the deal workspace. */}
              {activeTab === 'activity' && (
                <div
                  role="tabpanel"
                  id="customer-tabpanel-activity"
                  aria-labelledby="customer-tab-activity"
                  className="space-y-3"
                >
                  {/* Jobs-first timeline (V107.53) — replaces the old flat
                      activity row list that dumped every micro-event (job
                      created / fault created / stage change) at equal weight.
                      Hero job cards open inline (notes, photos, field log,
                      reasoning); faults / deals / calls thread around them
                      and cross-link via clickable chips. Fullscreen for room. */}
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] text-white/45 leading-snug">
                      Jobs lead the timeline — tap any card to open its notes, photos &amp; field log. Faults, deals, calls &amp; notes thread in around them.
                    </p>
                    <button
                      type="button"
                      onClick={() => setHistoryModalOpen(true)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-secondary/15 hover:bg-brand-secondary/25 text-brand-secondary border border-brand-secondary/30 text-xs font-semibold transition-colors"
                      title="Open the full-screen history timeline"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                      Fullscreen
                    </button>
                  </div>

                  {/* Add a reference note — sits in the timeline, filterable via
                      the Notes chip. Office-side memory that follows the customer. */}
                  <div className="relative flex items-start gap-2 p-3 rounded-lg bg-surface-glass border border-theme">
                    <textarea
                      ref={noteDraftRef}
                      value={noteDraft}
                      onChange={(e) => { setNoteDraft(e.target.value); syncNoteMentions(e.target.value); }}
                      onSelect={(e) => syncNoteMentions(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') { setShowNoteMentions(false); return; }
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAddNote(); }
                      }}
                      placeholder="Add a note about this customer… use @ to tag someone (⌘/Ctrl+Enter to save)"
                      rows={2}
                      className="flex-1 resize-none bg-transparent text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none"
                    />
                    {showNoteMentions && noteMentionMatches.length > 0 && (
                      <div className="absolute left-3 right-3 top-full mt-1 z-30 bg-surface-card border border-theme rounded-lg shadow-xl max-h-48 overflow-y-auto">
                        {noteMentionMatches.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertNoteMention(u)}
                            className="w-full text-left px-3 py-1.5 text-xs text-theme-primary hover:bg-surface-hover flex items-center gap-2"
                          >
                            <AtSign className="w-3 h-3 text-brand-secondary" />
                            <span>{u.name}</span>
                            {u.role && <span className="ml-auto text-[10px] text-theme-muted">{u.role}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleAddNote}
                      disabled={savingNote || !noteDraft.trim()}
                      className="shrink-0 self-end inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 border border-amber-500/30 text-xs font-semibold transition-colors disabled:opacity-40"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      {savingNote ? 'Saving…' : 'Add note'}
                    </button>
                  </div>

                  <CustomerHistoryTimeline
                    jobs={jobs}
                    faults={customerFaults}
                    deals={deals}
                    services={services}
                    serviceRequests={serviceRequests}
                    calls={callLogs}
                    notes={customerNotes}
                    auditEntries={aggregates?.auditEntries}
                    // A customer with no history is exactly where the operator
                    // wants to start something. Inline cards rather than the
                    // header's dropdown: in an empty state the options should
                    // be visible, and a popup anchored inside this scroll
                    // container gets clipped on a short viewport.
                    emptyStateAction={<CustomerCreateActions onSelect={handleCreate} />}
                  />
                </div>
              )}

              {/* Files Tab — aggregated attachments from all linked records */}
              {activeTab === 'files' && customer.id && (
                <div
                  role="tabpanel"
                  id="customer-tabpanel-files"
                  aria-labelledby="customer-tab-files"
                >
                  <AttachmentsSection
                    attachments={customer.attachments || []}
                    source={{
                      type: 'customer',
                      id: customer.id,
                      label: customer.name || 'Customer',
                    }}
                    currentEntityType="customer"
                    title="Files & Photos"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer removed (Phase 2) — slide-over close is the header X / backdrop click / Esc. */}
      </div>

      {/* Detail Modals */}
      <CallDetailModal
        isOpen={selectedCall !== null}
        onClose={() => setSelectedCall(null)}
        call={selectedCall}
      />
      <JobDetailModal
        isOpen={selectedJob !== null}
        onClose={() => setSelectedJob(null)}
        job={selectedJob}
      />
      {customer && (
        <ContactFormModal
          isOpen={isContactFormOpen}
          onClose={() => {
            setIsContactFormOpen(false);
            setEditingContact(null);
          }}
          customerId={customer.id!}
          customerName={customer.name || ''}
          contact={editingContact}
          addresses={customerAddresses}
        />
      )}
      {customer && (
        <AddCustomerServiceModal
          isOpen={isAddServiceModalOpen}
          onClose={() => setIsAddServiceModalOpen(false)}
          customerId={customer.id!}
          customerName={customer.name || ''}
          customerAddress={customer.address}
          // Services real-time subscription picks up the new doc — no
          // explicit refetch needed.
          onServiceAdded={() => {}}
        />
      )}
      <EditServiceModal
        isOpen={editServiceTarget !== null}
        onClose={() => setEditServiceTarget(null)}
        service={editServiceTarget}
        // Real-time subscription handles the refresh.
        onSaved={() => {}}
      />

      {/* Fault detail view */}
      <FaultDetailsView
        fault={selectedFault!}
        isOpen={selectedFault !== null}
        onClose={() => setSelectedFault(null)}
      />

      {/* Deal quick view — replaces the prior window.open new-tab flow so
          deals open in-modal (symmetric with jobs/faults). Same-tab navigate
          on "Open full workspace". */}
      <DealQuickViewModal
        dealId={selectedDealId}
        onOpenFullWorkspace={(id) => navigate(`/home/deals/${id}`)}
        onClose={() => setSelectedDealId(null)}
      />

      {/* Edit Customer Modal */}
      {customer && (
        <EditCustomerModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          customer={customer}
          onSaved={(updated) => {
            // Always refresh the locally-displayed record so navigated-to
            // children stay in sync (parent's onCustomerUpdated only
            // applies to the seed customer's list).
            setCustomer(updated);
            if (onCustomerUpdated) onCustomerUpdated(updated);
          }}
        />
      )}

      {/* Add Site — pre-selects this company as parent */}
      {customer && customer.isCompany && (
        <AddCustomerModal
          isOpen={isAddSiteOpen}
          onClose={() => setIsAddSiteOpen(false)}
          defaultParentCompanyId={customer.id}
          onCreated={(created) => {
            setChildCustomers((prev) =>
              [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
            );
          }}
        />
      )}

      {/* Link existing customer as a site */}
      {customer && customer.isCompany && (
        <LinkExistingCustomerModal
          isOpen={isLinkExistingOpen}
          onClose={() => setIsLinkExistingOpen(false)}
          company={customer}
          onLinked={(linked) => {
            setChildCustomers((prev) => {
              // Replace if the customer was linked from another company; otherwise append
              const idx = prev.findIndex((c) => c.id === linked.id);
              const next = idx >= 0
                ? prev.map((c, i) => (i === idx ? linked : c))
                : [...prev, linked];
              return next.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            });
          }}
        />
      )}

      {/* Intake modals behind the Create menu, + Log Call (Phase 5). All three
          take preSelectedCustomerId so the customer search step is skipped,
          and all three refetch + land the user on Activity on success so the
          record they just raised is visible without a close-and-reopen. */}
      <FaultIntakeModal
        isOpen={isFaultIntakeOpen}
        onClose={() => setIsFaultIntakeOpen(false)}
        preSelectedCustomerId={customer?.id}
        onCreated={handleRecordCreated}
      />
      <DealCreationWizard
        isOpen={isDealWizardOpen}
        onClose={() => setIsDealWizardOpen(false)}
        preSelectedCustomerId={customer?.id}
        onSuccess={handleRecordCreated}
      />
      <ServiceRequestIntakeModal
        isOpen={isServiceRequestIntakeOpen}
        onClose={() => setIsServiceRequestIntakeOpen(false)}
        preSelectedCustomerId={customer?.id}
        onCreated={handleRecordCreated}
      />
      <LogCallModal
        isOpen={isLogCallOpen}
        onClose={() => setIsLogCallOpen(false)}
        customer={customer}
        // Refresh the activity timeline so the just-logged call shows up
        // immediately without forcing the user to close + reopen the
        // modal. Calls aren't on a real-time subscription (jobs / deals /
        // faults / calls are one-shot loaded by loadCustomerData).
        onLogged={() => {
          loadCustomerData();
        }}
      />

      {/* Full-screen history timeline (V107.53) — launched from the Activity
          tab's Fullscreen button. Reuses the already-loaded arrays so there's
          no extra fetch; its deep-links navigate via router (closing it). */}
      <CustomerHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        customerName={customer.name}
        jobs={jobs}
        faults={customerFaults}
        deals={deals}
        services={services}
        serviceRequests={serviceRequests}
        calls={callLogs}
        notes={customerNotes}
        auditEntries={aggregates?.auditEntries}
      />

      {/* Themed confirm — replaces every window.prompt / window.confirm in
          this component. See pendingConfirm + runConfirm. */}
      {confirmConfig && (
        <ConfirmModal
          open={pendingConfirm !== null}
          title={confirmConfig.title}
          description={confirmConfig.description}
          confirmLabel={confirmConfig.confirmLabel}
          variant={confirmConfig.variant}
          requireReason={confirmConfig.requireReason}
          reasonLabel={confirmConfig.reasonLabel}
          reasonPlaceholder={confirmConfig.reasonPlaceholder}
          reasonRequired={confirmConfig.reasonRequired}
          loading={confirmLoading}
          onConfirm={runConfirm}
          onClose={() => {
            if (!confirmLoading) setPendingConfirm(null);
          }}
        />
      )}
    </div>
  );
}
